import { nanoid } from 'nanoid';
import { GameData, Song, Chart } from './models/SongData';
import { times } from './utils';
import { DrawnChart, EligibleChart, Drawing } from './models/Drawing';
import { ConfigState } from './config-state';
import { getDifficultyColor } from './hooks/useDifficultyColor';
import { getDiffAbbr } from './game-data-utils';
import { getAvailableLevels } from './game-data-utils';
import { myCustomDraw } from './card-draw-mine';

export function getDrawnChart(
  gameData: GameData,
  currentSong: Song,
  chart: Chart,
): EligibleChart {
  return {
    name: currentSong.name,
    jacket: currentSong.jacket,
    nameTranslation: currentSong.name_translation,
    artist: currentSong.artist,
    artistTranslation: currentSong.artist_translation,
    bpm: currentSong.bpm ?? '0',
    level: chart.lvl,
    levelConstant: chart.levelConstant ?? 0,
    flags: (chart.flags || []).concat(currentSong.flags || []),
    song: currentSong,
    // Fill in variant data per game
    diffAbbr: getDiffAbbr(gameData, chart.diffClass),
    diffColor: getDifficultyColor(gameData, chart.diffClass),
  };
}

/** returns true if song matches configured flags */
export function songIsValid(
  config: ConfigState,
  song: Song,
  forPocketPick = false,
): boolean {
  if (forPocketPick && !config.constrainPocketPicks) {
    return true;
  }
  return (
    (!song.flags || song.flags.every((f) => config.flags.has(f))) &&
    config.categories.has(song.category)
  );
}

/** returns true if chart matches configured difficulty/style/lvl/flags */
export function chartIsValid(
  config: ConfigState,
  chart: Chart,
  forPocketPick = false,
): boolean {
  if (forPocketPick && !config.constrainPocketPicks) {
    return true;
  }
  const implies = (a: boolean, b: boolean) => !a || b;

  const levelMetric = config.useLevelConstants
    ? chart.levelConstant ?? 0
    : chart.lvl;
  return (
    config.difficulties.has(chart.diffClass) &&
    implies(config.textCardDrawDistribution.trim() == '', levelMetric >= config.lowerBound &&
    levelMetric <= config.upperBound) &&
    (!chart.flags || chart.flags.every((f) => config.flags.has(f)))
  );
}

export function* eligibleCharts(config: ConfigState, gameData: GameData) {
  for (const currentSong of gameData.songs) {
    if (!songIsValid(config, currentSong)) {
      continue;
    }
    const charts = currentSong.charts;

    for (const chart of charts) {
      if (!chartIsValid(config, chart)) {
        continue;
      }

      // add chart to deck
      yield getDrawnChart(gameData, currentSong, chart);
    }
  }
}

/**
 * Produces a drawn set of charts given the song data and the user
 * input of the html form elements.
 * @param songs The song data (see `src/songs/`)
 * @param configData the data gathered by all form elements on the page, indexed by `name` attribute
 */
export function draw(gameData: GameData, configData: ConfigState): Drawing {
  return drawOngeki(gameData, configData, []);
}

/**
 * is this an accurate F-Y shuffle? who knows!?!
 */
export function shuffle<Item>(arr: Array<Item>): Array<Item> {
  const ret = arr.slice();
  for (let i = 0; i < ret.length; i++) {
    const randomUpcomingIndex =
      i + Math.floor(Math.random() * (ret.length - i));
    const currentItem = ret[i];
    ret[i] = ret[randomUpcomingIndex];
    ret[randomUpcomingIndex] = currentItem;
  }
  return ret;
}

/**
 * Produces a drawn set of charts given the song data and the user
 * input of the html form elements.
 * @param songs The song data (see `src/songs/`)
 * @param configData the data gathered by all form elements on the page, indexed by `name` attribute
 */
export function drawOngeki(
  gameData: GameData,
  configData: ConfigState,
  alreadyDrawnCharts: DrawnChart[],
): Drawing {
  const {
    chartCount: numChartsToRandom,
    useWeights,
    forceDistribution,
    weights,
    groupSongsAt,
    defaultPlayersPerDraw,
    textCardDrawDistribution,
  } = configData;

  if (textCardDrawDistribution.trim() == "") {
    return drawOriginal(gameData, configData, alreadyDrawnCharts);
  }

  // because of weirdness with floating point numbers, use this
  const levelInteger = (t: number) => Math.round(t*10);
  
  const validCharts = new Map<number, Array<EligibleChart>>();
  for (const chart of eligibleCharts(configData, gameData)) {
    let levelMetric = levelInteger(chart.levelConstant!);
    if (!validCharts.has(levelMetric)) {
      validCharts.set(levelMetric, []);
    }
    validCharts.get(levelMetric)!.push(chart);
  }

  console.log(validCharts);

  let newCharts = myCustomDraw(
    numChartsToRandom, 
    textCardDrawDistribution,
    alreadyDrawnCharts.map(chart => ({ level: chart.levelConstant! })),
    alreadyDrawnCharts,
    (_low, _high): DrawnChart | null => {
      let arr = [];
      let low = levelInteger(_low);
      let high = levelInteger(_high);
      for (let i=low; i<=high; i++) {
        arr.push(i);
      }
      let lists = arr.map(i => validCharts.get(i)!).filter(i => {
        if (i === undefined) {
          console.log("Warning: Undefined difficulty array", i)
        }
        return (i !== undefined);
      });
      // temp for debugging
      console.log(low, high, arr, [...validCharts.keys()], lists);

      const totalLength = lists.reduce((sum, list) => sum + list.length, 0);
      if (totalLength === 0) return null;

      const randomIndex = Math.floor(Math.random() * totalLength);

      let index = randomIndex;
      for (const list of lists) {
          if (index < list.length) {
            console.log("Selecting index", index, list.length)
            let randomChart = list.splice(index, 1)[0];
            return {
              ...randomChart,
              // Give this random chart a unique id within this drawing
              id: `drawn_chart-${nanoid(5)}`,
            };
          }
          index -= list.length;
      }

      return null;
    }
  );

  return {
    id: `draw-${nanoid(10)}`,
    charts: newCharts,
    players: times(defaultPlayersPerDraw, () => ''),
    bans: [],
    protects: [],
    pocketPicks: [],
    winners: [],
  };
}


function drawOriginal(gameData: GameData, configData: ConfigState, alreadyDrawnCharts: DrawnChart[]): Drawing {
  const {
    chartCount: numChartsToRandom,
    useWeights,
    forceDistribution,
    weights,
    groupSongsAt,
    defaultPlayersPerDraw,
  } = configData;

  const validCharts = new Map<number, Array<EligibleChart>>();
  const availableLevels = getAvailableLevels(gameData);
  /** all charts we will consider to be valid for this draw */
  availableLevels.forEach((level) => {
    validCharts.set(level, []);
  });

  // take already chosen charts, remove from pool
  // calculate exact number of charts to generate the pool
  //    each one gets a minimum number
  //    the remaining are selected randomly (????)
  // remove counts of already generated charts
  // generate the remaining charts

  for (const chart of eligibleCharts(configData, gameData)) {
    let levelMetric = chart.level;
    // merge in higher difficulty charts into a single group, if configured to do so
    if (useWeights && groupSongsAt && groupSongsAt < levelMetric) {
      levelMetric = groupSongsAt;
    }
    validCharts.get(levelMetric)?.push(chart);
  }

  /**
   * the "deck" of difficulty levels to pick from
   */
  let distribution: Array<number> = [];
  /**
   * Total amount of weight used, so we can determine expected outcome below
   */
  let totalWeights = 0;
  /**
   * Maximum number of charts we can expect to draw of each level. Only used with `forceDistribution`
   */
  const maxDrawPerLevel: Record<string, number> = {};
  /**
   * List of difficulty levels that must be picked first, to meet minimums. Only used with `forceDistribution`
   */
  const requiredDrawDifficulties: number[] = [];

  // build an array of possible levels to pick from
  for (const level of availableLevels) {
    let weightAmount = 0;
    if (useWeights) {
      weightAmount = weights[level] ?? 0;
      totalWeights += weightAmount;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weightAmount = validCharts.get(level)!.length;
    }
    times(weightAmount, () => distribution.push(level));
  }

  // If custom weights are used, expectedDrawsPerLevel[level] will be the maximum number
  // of cards of that level allowed in the card draw.
  // e.g. For a 5-card draw, we increase the cap by 1 at every 100%/5 = 20% threshold,
  // so a level with a weight of 15% can only show up on at most 1 card, a level with
  // a weight of 30% can only show up on at most 2 cards, etc.
  if (useWeights && forceDistribution) {
    let realChartsToRandom = numChartsToRandom + alreadyDrawnCharts.length;
    
    // fill requiredDrawDifficulties
    for (const level of availableLevels) {
      const levelAsStr = level.toString();
      const normalizedWeight = (weights[level] ?? 0) / totalWeights;
      maxDrawPerLevel[levelAsStr] = Math.ceil(
        normalizedWeight * realChartsToRandom,
      );
      // console.log(levelAsStr, level, weights, weights[level], totalWeights, normalizedWeight, maxDrawPerLevel[levelAsStr]);
      // setup minimum draws
      for (let i = 0; i < maxDrawPerLevel[levelAsStr]; i++) {
        requiredDrawDifficulties.push(level);
      }
    }
    console.log(requiredDrawDifficulties);
    console.log(alreadyDrawnCharts, realChartsToRandom, numChartsToRandom);
    console.assert(requiredDrawDifficulties.length == realChartsToRandom);

    for (let chart of alreadyDrawnCharts) {
      let index = requiredDrawDifficulties.findIndex(c => (c == chart.level));
      console.log(requiredDrawDifficulties, chart.level, chart.levelConstant);
      if (index != -1) {
        requiredDrawDifficulties.splice(index, 1);
      }
    }

    console.assert(requiredDrawDifficulties.length == numChartsToRandom);
  }

  const drawnCharts: DrawnChart[] = [];
  /**
   * Record of how many songs of each difficulty have been drawn so far
   */
  const difficultyCounts: Record<string, number> = {};

  while (drawnCharts.length < numChartsToRandom) {
    if (distribution.length === 0) {
      // no more songs available to pick in the requested range
      // will be returning fewer than requested number of charts
      break;
    }

    // first pick a difficulty (with priority to minimum draws)
    let chosenDifficulty = requiredDrawDifficulties.shift();
    if (!chosenDifficulty) {
      chosenDifficulty =
        distribution[Math.floor(Math.random() * distribution.length)];
    }
    if (useWeights && groupSongsAt && groupSongsAt < chosenDifficulty) {
      chosenDifficulty = groupSongsAt;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const selectableCharts = validCharts.get(chosenDifficulty)!;
    const randomIndex = Math.floor(Math.random() * selectableCharts.length);
    const randomChart = selectableCharts[randomIndex];

    if (randomChart) {
      // Save it in our list of drawn charts
      drawnCharts.push({
        ...randomChart,
        // Give this random chart a unique id within this drawing
        id: `drawn_chart-${nanoid(5)}`,
      });
      // remove drawn chart from deck so it cannot be re-drawn
      selectableCharts.splice(randomIndex, 1);
      if (!difficultyCounts[chosenDifficulty]) {
        difficultyCounts[chosenDifficulty] = 1;
      } else {
        difficultyCounts[chosenDifficulty]++;
      }
    }

    // check if maximum number of expected occurrences of this level of chart has been reached
    const reachedExpected =
      forceDistribution &&
      difficultyCounts[chosenDifficulty.toString()] ===
        maxDrawPerLevel[chosenDifficulty.toString()];

    if (selectableCharts.length === 0 || reachedExpected) {
      // can't pick any more songs of this difficulty
      distribution = distribution.filter((n) => n !== chosenDifficulty);
    }
  }

  return {
    id: `draw-${nanoid(10)}`,
    charts: drawnCharts, // shuffle(drawnCharts),
    players: times(defaultPlayersPerDraw, () => ''),
    bans: [],
    protects: [],
    pocketPicks: [],
    winners: [],
  };
}